import React, { useEffect } from "react";
import { connect } from "react-redux";
import { Descriptions, Table, Tag, Space, Button, Divider } from 'antd';
import { FlagFilled, BranchesOutlined, LockFilled, BarcodeOutlined, InfoCircleFilled } from '@ant-design/icons';
import styled from 'styled-components';

import supplyChainIdentification from './mocks/supplyChainIdentification';
import cahraDetermination from './mocks/cahraDetermination';
import supplyChainEvaluation from './mocks/supplyChainEvaluation';
import dueDiligenceCompliance from './mocks/dueDiligenceCompliance';
import continousRisk from './mocks/continousRisk';
import riskMitigation from './mocks/riskMitigation';
import reportingGuidelines from './mocks/reportingGuidelines';
import PageHeader from "../../../../../components/Layout/PageHeader";
import { getLocales } from "../../../../../components/Table/newTable";

const Styles = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: start;
	.ant-page-header {
		padding: 8px 8px 8px var(--size);
		border-bottom: 1px solid rgba(0, 0, 0, 0.1);
		.ant-page-header-heading {
			justify-content: flex-start;
		}
		.ant-page-header-heading-extra {
			display: flex;
			margin-left: var(--size);
		}
		.ant-btn {
			display: flex;
			span[role="img"] {
				font-size: 18px;
			}
		}
	}
	.ant-descriptions-title {
		font-weight: 400;
		color: #1482e9;
	}
	.content {
		padding: var(--size);
		h5 {
			font-weight: 400;
		}
	}
	.ant-table-wrapper {
		margin: var(--size) 0;
	}
	.ant-table-cell {
		a {
			color: #1482e9;
			text-decoration: underline;
			font-weight: 500;
		}
		&:last-of-type {
			display: flex;
			flex-direction: row;
			justify-content: center;
			align-items: center;
		}
	}
	.ant-divider {
		background: rgba(0, 0, 0, 0.4);
		margin: 36px 0;
	}
	.ant-space {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		margin: var(--size) 0;
		border: 1px solid #f0f0f0;
		padding: 12px;
		span[role="img"] {
			height: 27px;
			font-size: 26px;
			margin-right: 12px;
		}
		h5 {
			margin: 0;
		}
		.ant-space-item {
			> div {
				display: flex;
				flex-direction: row;
				justify-content: flex-start;
				align-items: center;
			}
		}
		&.no-border {
			border: none;
		}
	}
	.ant-descriptions-item-content {
		display: flex;
		flex-direction: row;
	}
	.color-red {
		color: red;
	}
	.button-container {
		display: flex;
		justify-content: center;
		.mr2 {
			margin-right: 2em;
		}
		button {
			flex: 1 1 0;
			max-width: 200px;
		}
	}
	.section-title {
		font-size: var(--font-size-lg);
		line-height: 1.5715;
		margin-bottom: 20px;
		font-weight: 400;
    color: #1482e9;
	}
	.ant-btn {
		box-shadow: 0 3px 1px -2px rgba(0,0,0,.2),
		0 2px 2px 0 rgba(0,0,0,.14),
		0 1px 5px 0 rgba(0,0,0,.12);
		font-size: 11px;
		text-transform: uppercase;
		font-weight: 500;
	}
`;

const mstp = ({ authentication }) => ({
  authentication
});
function CADDComplianceReport() {
  const mineSiteOfOrigin = ['Liogo', 'Mayolo', 'Frigo', 'Magnida'];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const cell = document.querySelectorAll('.ant-table-cell')[9];
    const button = cell.querySelector('button');
    const { right } = button.getBoundingClientRect();
    const spaces = document.querySelectorAll('.ant-space');
    const spaceButtons = Array.from(spaces)
      .map(space => space.querySelector('button'));
    spaceButtons.forEach(b => {
      if (b) {
        const { right: bRight } = b.getBoundingClientRect();
        const margin = - (right - bRight);
        b.setAttribute('style', `margin-right: ${margin}px`);
      }
    })
  }, [])

  return <Styles>
    <PageHeader title="Compliance Report" />
    <div className="content" role="main">
      <Descriptions title="Shipment Identification" bordered size="small">
        <Descriptions.Item label="Shipment ID" span={1.5}>SW718822221</Descriptions.Item>
        <Descriptions.Item label="Supplier" span={1.5}>International Trading SA</Descriptions.Item>
        <Descriptions.Item label="Project ID" span={1.5}>CADD-DRC-001</Descriptions.Item>
        <Descriptions.Item label="Exporter" span={1.5}>Confidential</Descriptions.Item>
        <Descriptions.Item label="Materials" span={1.5}>Gold - Powder</Descriptions.Item>
        <Descriptions.Item label="Export Date" span={1.5}>-</Descriptions.Item>
        <Descriptions.Item label="Total Weight" span={1.5}>3.787 kg</Descriptions.Item>
        <Descriptions.Item label="Country of Origin" span={1.5}>DR Congo</Descriptions.Item>
      </Descriptions>
      <Divider />
      <h5 className="section-title">Project - Onboarding</h5>
      <Table
        columns={supplyChainIdentification.columns}
        dataSource={supplyChainIdentification.data}
        bordered
        size="small"
        tableLayout="fixed"
        pagination={false} />
      <Space>
        <h6>
          <span className="color-red">(*)</span>
          YOUR DIRECT SUPPLIER HAS OPTED FOR NON-DISCLOSURE OF COMMERCIALLY SENSITIVE INFORMATION WHICH DOES NOT AFFECT COMPLIANCE
        </h6>
      </Space>
      <Table
        columns={cahraDetermination.columns}
        dataSource={cahraDetermination.data}
        bordered
        size="small"
        tableLayout="fixed"
        pagination={false} />
      <Space>
        <div>
          <FlagFilled style={{ color: "red" }} />
          <h5 style={{ color: "red" }}>
            THE SUPPLY CHAIN REQUIRES EVIDENCE OF <u>RISK READINESS</u> AND <u>RISK MANAGEMENT</u>
          </h5>
        </div>
        <Button size="small" type="primary">Details</Button>
      </Space>
      <Divider />
      <Descriptions title="Supply Chain - Risk Readiness" bordered size="small">
        <Descriptions.Item label="Province(s) of Origin" span={3}>Ituri</Descriptions.Item>
        <Descriptions.Item label="Mine Site(s) of Origin" span={3}>
          {mineSiteOfOrigin.map((m, i) => (
            <Tag key={i} style={{ display: 'flex', alignItems: 'center', width: 'fit-content' }}>
              <a
                href="https://www.ipisresearch.be/mapping/webmapping/drcongo/v6/#1.5979602212995445/30.178070068359375/7.675534606718225/4/1/"
                target="_blank" rel="noopener noreferrer" style={{ marginRight: '5px' }}>{m}</a>
              <InfoCircleFilled />
            </Tag>
          ))}
        </Descriptions.Item>
        <Descriptions.Item label="Number of Evaluated Participants" span={3}>
          <span style={{ color: 'red' }}>9</span>/10
        </Descriptions.Item>
        <Descriptions.Item label="Auxiliary Information Source(s)" span={3}>Justice Plus &nbsp;&nbsp;<InfoCircleFilled style={{ lineHeight: '20px' }} /></Descriptions.Item>
      </Descriptions>
      <Table
        columns={supplyChainEvaluation.columns}
        dataSource={supplyChainEvaluation.data}
        bordered
        size="small"
        tableLayout="fixed"
        pagination={false} />
      <Space>
        <div>
          <BranchesOutlined style={{ color: "#23af52" }} />
          <h5 style={{ color: "#23af52" }}>
            ALL INFORMATION NECESSARY FOR THE EVALUATION OF THE SUPPLY CHAIN HAS BEEN PROVIDED
          </h5>
        </div>
        <Button size="small" type="primary">Details</Button>
      </Space>
      <Table
        columns={dueDiligenceCompliance.columns}
        dataSource={dueDiligenceCompliance.data}
        bordered
        size="small"
        tableLayout="fixed"
        pagination={false} />
      <Space>
        <div>
          <LockFilled style={{ color: "#23af52" }} />
          <h5 style={{ color: "#23af52" }}>
            DUE DILIGENCE MANAGEMENT SYSTEMS ARE ESTABLISHED AND DOCUMENTED
          </h5>
        </div>
        <Button size="small" type="primary">Details</Button>
      </Space>
      <Divider />
      <Descriptions title="Shipment - Risk Management" bordered size="small">
        <Descriptions.Item label="Province(s) of Origin" span={3}>Ituri</Descriptions.Item>
        <Descriptions.Item label="Mine Site(s) of Origin" span={3}>
          <Tag style={{ display: 'flex', alignItems: 'center', width: 'fit-content' }}>
            <a href="https://www.ipisresearch.be/mapping/webmapping/drcongo/v6/#1.5979602212995445/30.178070068359375/7.675534606718225/4/1/" target="_blank" rel="noopener noreferrer" style={{ marginRight: '5px' }}>Liogo</a>
            <InfoCircleFilled />
          </Tag>
          <Tag style={{ display: 'flex', alignItems: 'center', width: 'fit-content' }}>
            <a href="https://www.ipisresearch.be/mapping/webmapping/drcongo/v6/#1.5979602212995445/30.178070068359375/7.675534606718225/4/1/" target="_blank" rel="noopener noreferrer" style={{ marginRight: '5px' }}>Firgo</a>
            <InfoCircleFilled />
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Number of Evaluated Participants" span={3}>
          <span style={{ color: "#23af52" }}>7</span>/7
        </Descriptions.Item>
        <Descriptions.Item label="Auxiliary Information Source(s)" span={3}>Justice Plus</Descriptions.Item>
      </Descriptions>
      <Table
        columns={continousRisk.columns}
        dataSource={continousRisk.data}
        bordered
        size="small"
        tableLayout="fixed"
        pagination={false} />
      <Table
        columns={riskMitigation.columns}
        dataSource={riskMitigation.data}
        bordered
        size="small"
        tableLayout="fixed"
        pagination={false} />
      <Space>
        <div>
          <BarcodeOutlined style={{ color: "#23af52" }} />
          <h5 style={{ color: "#23af52" }}>
            SUPPLY CHAIN RISK IS MANAGED IN ACCORDANCE WITH INTERNATIONAL STANDARDS
          </h5>
        </div>
        <Button size="small" type="primary">Details</Button>
      </Space>
      <Divider />
      <h5 className="section-title">Compliant Reporting</h5>
      <Table
        columns={reportingGuidelines.columns}
        dataSource={reportingGuidelines.data}
        bordered
        size="small"
        tableLayout="fixed"
        pagination={false} />
      <Space className="no-border"> &nbsp; </Space>
      <div className="button-container">
        <Button className="mr2" type="primary">Approve</Button>
        <Button type="default">Reject</Button>
      </div>
      <Space className="no-border"> &nbsp; </Space>
    </div>
  </Styles>
}


export default connect(mstp)(CADDComplianceReport);